import React, { useState, useEffect, useId, useRef } from 'react';

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  className?: string;
  prefix?: string;
  suffix?: string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 2000,
  className = '',
  prefix = '',
  suffix = ''
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);
  // Identificador estável só para a chave da keyframe CSS abaixo — a observação usa a ref
  // diretamente (não document.getElementById), então múltiplos contadores com o mesmo `value`
  // na mesma página (ex.: dois números de prova social iguais) não colidem.
  const instanceId = useId();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * value);
      
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, value, duration]);

  return (
    <span
      ref={elementRef}
      id={`counter-${instanceId}`}
      className={`font-bold transition-all duration-300 ${className}`}
      style={{
        animation: isVisible ? 'counterBounce 0.6s ease-out' : 'none',
        transform: isVisible ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s ease-out'
      }}
    >
      {prefix}{count.toLocaleString()}{suffix}
      
      {/* CSS para animações personalizadas */}
      <style>{`
        @keyframes counterBounce {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        /* Animação de piscar removida */
      `}</style>
    </span>
  );
};

export default AnimatedCounter;
